import EventTimesHtml from './event.times.html';
import WindowFlipHtml from '../../app-lite/core/templates/window-flip.html';

export function EventService (
  $q,
  $state,
  $uibModal,
  EventTotals,
  jsonApi,
  messages,
  pruneApi,
  workshopAPI
) {
  const _defaultItem = {
    amount                : 0.00,
    description           : '',
    gst                   : 0.00,
    gst_free              : false,
    hours_worked          : 0,
    id                    : '',
    event_id              : '',
    item_code             : '',
    job_card_note         : '',
    note                  : '',
    product_id            : '',
    product_type          : 'W',
    quantity              : 0,
    requires_serial_number: false,
    unit_cost             : 0.00,
    unit_price            : 0.00,
    untouched             : true
  };

  var Event = function (event, taxGroups = []) {
    this.event     = event;
    this.form      = {};
    this.taxGroups = taxGroups;
    this.totals    = new EventTotals(event);
  };

  /**
   * Before we do any saves, the array of items to delete
   * needs to be added to the list of items. Also, we need
   * to remove all untouched items
   * @param  {object} event   Event to prepare for save
   * @return {object}         Assembled event
   */
  Event.prototype.assemble = function () {
    const self = this;

    const event = jsonApi.copy(self.event);

    event.event_items_attributes = pruneApi.removeUntouched(event.event_items_attributes);
    event.event_items_attributes = event.event_items_attributes.concat(event.itemsToDelete);

    if (event.tax_group_id) {
      event.tax_group_name = self.taxGroups.filter(group => group.id === event.tax_group_id)[0].name;
    }

    return $q.when(event);
  };

  Event.prototype.closed = function () {
    var self = this;

    return self.event.event_status === 'C';
  };

  Event.prototype.status = function (status) {
    var self = this;
    var statusMap = {
      open  : 'O',
      closed: 'C',
    };

    return statusMap[status] === self.event.event_status;
  };

  /**
   * Handles all validation on the event.
   * Validates the form and then throws a specialized error if it
   * is invalid
   * @return {promise} The event if valid, nothing if invalid
   */
  Event.prototype.validate = function () {
    var self = this;
    var deferred = $q.defer();

    if (self.form.$valid) {
      deferred.resolve(self.event);
    }
    else {
      messages.clientError(self.form);
      deferred.reject();
    }

    return deferred.promise;
  };

  /**
   * After all saves, certain actions need to take place no matter what.
   * They are handled here
   * @param  {object} event    Newly saved event
   * @return {promise}         The new event
   */
  Event.prototype.cleanup = function (event, options) {
    var self = this;
    var deferred = $q.defer();

    // Set the event in the class to the new event
    self.event = event;

    // Set the form to pristine so page changes can happen without warning
    self.form.$setPristine();

    // Redirect to page with new id from event
    $state.go('app.event', {
      customer_id: event.customer_id,
      vehicle_id : event.vehicle_id,
      id         : event.id
    }, {
      inherit: false,
      // Reload the controller in case the event is already on the url
      reload : !options || !options.skipRefresh
    });

    deferred.resolve(event);

    return deferred.promise;
  };

  /**
   * Saves the event after removing untouched items,
   * adding the items to be deleted and validating it.
   * @return {promise} The newly saved event or the error
   */
  Event.prototype.save = function (options) {
    const self   = this;
    const method = self.event.id ? 'patch': 'post';
    const url    = self.event.id ? '/event': 'events';

    return self.validate()
    .then(() => self.assemble())
    .then(event => {
      return workshopAPI[method](url, event, 'event');
    }).then(event => {
      return self.cleanup(event, options);
    });
  };

  Event.prototype.showEventTimes = function (eventItem, eventTimeId) {
    return $uibModal.open({
      controller       : 'EventTimesCtrl',
      templateUrl      : EventTimesHtml,
      windowClass      : 'lg-modal flip-container',
      windowTemplateUrl: WindowFlipHtml,
      resolve          : {
        eventItem  : () => eventItem,
        eventTimeId: () => eventTimeId,
        eventTimes : () => workshopAPI.get('/event_times/index', [ eventItem.id ])
      }
    }).result;
  };

  Event.prototype.discountType = function(type) {
    var self = this;
    var typeMap = {
      cash      : 'C',
      percentage: 'P'
    };

    return typeMap[type] === self.event.discount_type;
  };

  Event.prototype.getInitialItem = function () {
    return angular.copy(_defaultItem);
  };

  Event.prototype.replaceItem = function ( item = {}, index = 0 ) {
    const self = this;

    const replacedItem = self.getInitialItem();

    /**
     * If a row is already part of a bundle, we want to maintain that.
     * Also, maintain the order.
     **/
    replacedItem.bom_parent_id = item.bom_parent_id;
    replacedItem.ordering      = item.ordering;

    self.event.event_items_attributes.splice(index, 0, replacedItem);
  };

  return Event;
}